import React, { useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react'; // React Grid Logic
import { ActivityIndicator } from '../ActivityIndicator';
import { alpineTheme } from './themes';

/* Exported (and defined outside the component, since it closes over nothing per-render) so a
   column's clipboard behavior can be tested directly, without mounting AG Grid. AG Grid calls this
   in place of a column's valueFormatter when copying, so a column whose valueGetter returns a Date
   (only the loss grid's Occurred column, today) needs the Date case handled here or a copy-paste
   round trip has no display format to read back. */
export const processCellForClipboard = (params) => {
  const { dataType } = params.column.colDef;
  const value = params.value;

  if (dataType === 'number' || params?.node?.data?.type === 'monthlyInventory') {
    return value ? value.toLocaleString()?.replace(/[$,]/g, '') : '';
  }
  // formatValue re-runs the column's own valueFormatter — reading the row's real stored value, not
  // this Date — so the clipboard gets exactly what the cell displays, and pasting it back is exactly
  // what the column's own valueSetter already knows how to read.
  if (value instanceof Date) {
    return params.formatValue(value);
  }
  return value;
};

export const Grid = (props) => {
  const onGridReady = (params) => {
    props.setGridReference(params);
    params.api.sizeColumnsToFit();
  };
  const { data, columns, gridOptions, pageSize = 10, floatingFilter = true, shouldShowPagination = true, loading } = props;

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 150,
      filter: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        buttons: ['reset', 'apply'],
        suppressMiniFilter: true,
      },
      floatingFilter: floatingFilter,
      enableCellChangeFlash: true,
    };
  }, [floatingFilter]);

  const paginationPageSizeSelector = useMemo(() => {
    return [10, 20, 50, 100, 300, 500];
  }, []);

  const processCellFromClipboard = useCallback((params) => {
    const { dataType } = params.column.colDef;

    if (dataType === 'number') return Number(params.value.replace(/[^0-9.]/g, ''));
    else if (params?.node?.data?.type === 'monthlyInventory') {
      const value = params.value;
      if (/\d/.test(value)) {
        return value.replace(/[^0-9.]/g, '');
      }
      return value;
    } else return params.value;
  }, []);

  return (
    <div id="ag-grid-table" style={{ height: !data.length && '120px', marginBottom: '10px' }}>
      <AgGridReact
        theme={alpineTheme}
        columnMenu="legacy"
        rowData={data}
        columnDefs={columns}
        headerHeight={36}
        defaultColDef={defaultColDef}
        loading={loading}
        {...props}
        onGridReady={onGridReady}
        pagination={!!data.length && shouldShowPagination}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={paginationPageSizeSelector}
        overlayNoRowsTemplate={'<div>No rows to show</div>'}
        suppressPropertyNamesCheck={true}
        gridOptions={gridOptions}
        cellSelection={props.cellSelection ?? true}
        undoRedoCellEditing={true}
        undoRedoCellEditingLimit={100}
        suppressRowClickSelection={true}
        processCellFromClipboard={processCellFromClipboard}
        processCellForClipboard={processCellForClipboard}
        loadingOverlayComponent="customSpinner"
        components={{
          customSpinner: ActivityIndicator,
        }}
      />
    </div>
  );
};
